import React from 'react';
import {
    CloseButton,
    Group,
    Popover,
    Title,
    Box,
    type FloatingPosition,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';

import { PropagationStopper } from '../../common';
import {
    CELL_DETAIL_ARROW_CLASSNAME,
    CELL_DETAIL_DROPDOWN_CLASSNAME,
} from './cellDetailSurfaceClasses';

type Props = {
    title: string;
    trigger: React.ReactNode;
    triggerClassName?: string;
    dropdownClassName?: string;
    constrainToAvailableSpace?: boolean;
    flip?: boolean;
    position?: FloatingPosition;
    children: React.ReactNode | ((ctx: { opened: boolean }) => React.ReactNode);
};

export function TableCellDetailPopover({
    title,
    trigger,
    triggerClassName,
    dropdownClassName,
    constrainToAvailableSpace = true,
    flip = true,
    position = 'bottom-start',
    children,
}: Props) {
    const [opened, setOpened] = React.useState(false);

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            withArrow
            arrowOffset={6}
            arrowSize={10}
            arrowPosition="center"
            position={position}
            offset={10}
            withinPortal
            floatingStrategy="fixed"
            middlewares={{
                flip,
                shift: { padding: 16 },
                size: constrainToAvailableSpace
                    ? {
                          padding: 16,
                      }
                    : false,
            }}
            shadow="md"
            zIndex={2500}
            trapFocus={false}
            transitionProps={{
                transition: 'fade',
                duration: 120,
            }}
            clickOutsideEvents={['mousedown', 'touchstart']}
            classNames={{
                dropdown: `${CELL_DETAIL_DROPDOWN_CLASSNAME} overflow-hidden flex flex-col max-h-[min(70vh,32rem)] ${
                    dropdownClassName ?? ''
                }`,
                arrow: CELL_DETAIL_ARROW_CLASSNAME,
            }}
        >
            <Popover.Target>
                <button
                    type="button"
                    className={`flex w-full max-w-full min-w-0 cursor-pointer items-center appearance-none ${
                        triggerClassName ?? ''
                    }`}
                    aria-expanded={opened}
                    onClick={event => {
                        event.stopPropagation();
                        setOpened(v => !v);
                    }}
                >
                    {trigger}
                </button>
            </Popover.Target>
            <Popover.Dropdown>
                <PropagationStopper className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <Group
                        justify="space-between"
                        gap="xs"
                        wrap="nowrap"
                        px="md"
                        pt="sm"
                        pb="xs"
                        className="border-b border-gray-100 shrink-0 sticky top-0 z-10 bg-white"
                    >
                        <Title
                            order={5}
                            className="text-gray-900 font-bold truncate"
                        >
                            {title}
                        </Title>
                        <CloseButton
                            className="shrink-0"
                            aria-label="Close details"
                            icon={<IconX size={16} stroke={1.5} />}
                            onClick={() => setOpened(false)}
                        />
                    </Group>
                    <Box className="min-h-0 flex-1 overflow-y-auto">
                        {typeof children === 'function'
                            ? children({ opened })
                            : children}
                    </Box>
                </PropagationStopper>
            </Popover.Dropdown>
        </Popover>
    );
}
